import React, { useState } from 'react';
import { Table, Button, Space, Tag, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRoadmaps, deleteRoadmap } from './api';
import RoadmapForm from './RoadmapForm';

interface Roadmap {
  id: number;
  title: string;
  description: string;
  goal: string;
  difficulty: string;
  duration: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: string;
  roadmapCourses: {
    id: number;
    order: number;
    isRequired: boolean;
    course: {
      id: number;
      title: string;
    };
  }[];
}

interface RoadmapsTableProps {
  onEditRoadmap: (roadmap: Roadmap) => void;
  onCreateRoadmap: () => void;
}

const RoadmapsTable: React.FC<RoadmapsTableProps> = ({ onEditRoadmap, onCreateRoadmap }) => {
  const queryClient = useQueryClient();

  const { data: roadmaps, isLoading } = useQuery({
    queryKey: ['roadmaps'],
    queryFn: getRoadmaps,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRoadmap,
    onSuccess: () => {
      message.success('Roadmap успешно удален');
      queryClient.invalidateQueries({ queryKey: ['roadmaps'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при удалении roadmap');
    },
  });

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'green';
      case 'intermediate':
        return 'orange';
      case 'advanced':
        return 'red';
      default:
        return 'default';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'Начинающий';
      case 'intermediate':
        return 'Средний';
      case 'advanced':
        return 'Продвинутый';
      default:
        return difficulty;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <div className="font-medium text-gray-900">{text}</div>
      ),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div className="text-gray-600 max-w-xs truncate" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: 'Сложность',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (difficulty: string) => (
        <Tag color={getDifficultyColor(difficulty)}>
          {getDifficultyText(difficulty)}
        </Tag>
      ),
    },
    {
      title: 'Длительность',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Курсов',
      key: 'coursesCount',
      render: (record: Roadmap) => (
        <span className="font-medium">{record.roadmapCourses.length}</span>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активен' : 'Неактивен'}
        </Tag>
      ),
    },
    {
      title: 'Дата создания',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (record: Roadmap) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEditRoadmap(record)}
            title="Редактировать"
          />
          <Popconfirm
            title="Удалить roadmap?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Удалить"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Roadmap</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onCreateRoadmap}
          size="large"
        >
          Создать roadmap
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={roadmaps}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} roadmap`,
        }}
      />
    </div>
  );
};

export default RoadmapsTable;
